import NavLinkAdapter from '@/@fuse/core/NavLinkAdapter/NavLinkAdapter';
import FuseUtils from '@/@fuse/utils';
import Icon from '@material-ui/core/Icon';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import { makeStyles } from '@material-ui/core/styles';
import Snackbar from '@material-ui/core/Snackbar';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import * as Actions from '@/app/store/actions';

const useStyles = makeStyles((theme) => ({
	item: {
		width: '100%',
		textTransform: 'capitalize',
		paddingRight: 12,
		paddingLeft: (props) => (props.itemPadding > 80 ? 80 : props.itemPadding),
		'&.active': {
			background: 'linear-gradient(270deg, rgba(41, 119, 233, 0.00) 0%, #29A4FF 50.7%, rgba(15, 136, 238, 0.97) 100%);',
			pointerEvents: 'none',
			transition: 'border-radius .15s cubic-bezier(0.4,0.0,0.2,1)',
			'& .list-item-text-primary': {
				color: 'inherit',
			},
			'& .list-item-icon': {
				color: 'inherit',
			},
		},
		'& .list-item-icon': {
			marginRight: 16,
			width: 24,
			height: 24,
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
		},
		'& .list-item-text': {},
		color: theme.palette.text.primary,
		cursor: 'pointer',
		textDecoration: 'none!important',
	},
	customIcon: {
		width: '100%',
		height: '100%',
		objectFit: 'contain',
	},
}));

const FuseNavVerticalItem = ({ item, nestedLevel }) => {
	const dispatch = useDispatch();
	const [iconError, setIconError] = useState(false);
	const [state, setState] = useState({
		open: false,
		vertical: 'top',
		horizontal: 'center',
	});

	const classes = useStyles({
		itemPadding: nestedLevel > 0 ? 40 + nestedLevel * 16 : 24,
	});

	const userRole = useSelector(({ auth }) => auth.user.role);
	const userPermission = useSelector(
		({ auth }) =>
			(auth.user.permissions ?? auth.user.data.permissions)?.map((permission) => permission.replace(/^\/+/i, '')) ?? []
	);

	const hasPermission = useMemo(() => FuseUtils.hasPermission(item.auth, userRole), [item.auth, userRole]);
	const { t } = useTranslation('navigation');

	const handleClose = (event, reason) => {
		if (reason === 'clickaway') {
			return;
		}
		setState({ ...state, open: false });
	};

	if (!userPermission.includes(item.permission) && !item.alwaysShow) {
		return null;
	}
	if (!hasPermission) {
		return null;
	}

	const renderIcon = () => {
		if (!item.icon) {
			return null;
		}

		if (item.isCustomIcon) {
			const iconPath = item.icon.startsWith('/') ? item.icon : `/assets/images/navbarIcons/${item.icon}`;
			return (
				<div className="list-item-icon shrink-0" style={{ width: 32, marginRight: 15 }}>
					<img
						alt={item.title}
						className={classes.customIcon}
						src={iconPath}
						style={{
							minWidth: '20px',
							minHeight: '20px',
							maxWidth: '20px',
							maxHeight: '20px',
						}}
					/>
				</div>
			);
		}

		return (
			<Icon className="list-item-icon shrink-0" color="action">
				{item.icon}
			</Icon>
		);
	};

	return (
		<ListItem
			button
			className={clsx(classes.item, 'flex text-[20px] my-16')}
			component={NavLinkAdapter}
			exact={item.exact}
			onClick={() => {
				if (userPermission.includes(item.permission)) {
					dispatch(Actions.navbarCloseMobile());
				} else {
					setState({ ...state, open: true });
				}
			}}
			to={item.url}
		>
			{renderIcon()}
			<ListItemText
				className="list-item-text"
				classes={{ primary: 'text-8 list-item-text-primary font-proxima' }}
				primary={item.translate ? t(item.translate) : item.title}
			/>

			{item.badge && (
				<div className="flex-shrink-0 ml-8">
					<div className={clsx('flex items-center justify-center h-20 w-24 rounded-sm', item.badge.bg)}>
						<div className="font-semibold">{item.badge.title}</div>
					</div>
				</div>
			)}

			<Snackbar
				anchorOrigin={{ vertical: state.vertical, horizontal: state.horizontal }}
				autoHideDuration={6000}
				message={`You are not authorized to use ${item.title}`}
				onClose={handleClose}
				open={state.open}
			/>
		</ListItem>
	);
};

FuseNavVerticalItem.propTypes = {
	item: PropTypes.shape({
		id: PropTypes.string.isRequired,
		title: PropTypes.string,
		icon: PropTypes.string,
		url: PropTypes.string,
		isCustomIcon: PropTypes.bool,
		exact: PropTypes.bool,
	}),
};

export default React.memo(FuseNavVerticalItem);
